import { Box, CircularProgress, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { CREATE_SURVEY_SERIES } from '../../constants';
import { buildOrderData } from '../../helpers/tableUtil';
import { SortDirection } from '../../models/global';
import { State } from '../../models/state';
import { getCCEmail, getSurveySeries } from '../../store/actions/survey';
import Button from '../FormComponents/Button';
import CCEmail from '../SurveyForm/CCEmail';
import Series from '../SurveyForm/Series';
import Table from '../Table/Table';
import './SurveySeries.scss';

const tableMap = new Map<string, string>([
  ['Name', 'name'],
  ['Notes', 'notes'],
]);
// const columns = Array.from(tableMap.keys());

function Surveys() {
  let columns;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const [seriesPopup, setSeriesPopup] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [ccEmailPopup, setCCEmailPopup] = useState(false);
  const [ccEmail, setCCEmail] = useState('');
  const { seriesList, seriesListCount } = useSelector((state: State) => state.survey);
  const { isAdmin } = useSelector((state: State) => state.auth.user);

  const [orderBy, setOrderBy] = useState('Name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const getData = useCallback(() => {
    const orderData = buildOrderData({
      orderBy,
      sortDirection,
      tableMap,
    });

    (() => setPageLoading(true))();
    dispatch(
      getSurveySeries(
        {
          limit: rowsPerPage,
          page: page + 1,
          orderData,
        },
        () => setPageLoading(false),
      ),
    );
  }, [dispatch, orderBy, page, sortDirection]);

  const getCCEmailData = useCallback(() => {
    dispatch(
      getCCEmail((ccEmail: string) => {
        setCCEmail(ccEmail);
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    getData();
    getCCEmailData();
  }, [getData, getCCEmailData]);

  if (isAdmin) {
    columns = ['Name'];
  } else {
    columns = ['Name', 'Notes'];
  }

  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <Box className='survey-series'>
      <Box className='survey-series-header' sx={{ mb: 2 }}>
        <Typography variant='h5'>Survey Series</Typography>
        {isAdmin && (
          <Box display='flex' alignItems='center' gap={5}>
            <Typography
              sx={{ cursor: 'pointer' }}
              variant='body2'
              color='#1976d2'
              onClick={() => setCCEmailPopup(true)}
            >
              Setup ITU CC Email
            </Typography>
            <Box className='create-button'>
              <Button text='Create' onClick={() => setSeriesPopup(true)} />
            </Box>
          </Box>
        )}
      </Box>
      <Table
        columns={columns}
        rows={seriesList}
        count={seriesListCount}
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        rowClickHandler={(id: string) => navigate(`/survey-series/${id}`)}
        orderBy={orderBy}
        handleOrder={setOrderBy}
        sortDirection={sortDirection}
        handleSortDirection={setSortDirection}
      />
      <Series
        open={seriesPopup}
        closePopup={() => {
          setSeriesPopup(false);
        }}
        getData={() => {
          if (page === 0) {
            getData();
          } else {
            setPage(0);
          }
        }}
        type={CREATE_SURVEY_SERIES}
      />
      <CCEmail
        open={ccEmailPopup}
        closePopup={() => {
          setCCEmailPopup(false);
        }}
        ccEmailValue={ccEmail}
        getCCEmailData={getCCEmailData}
      />
    </Box>
  );
}

export default Surveys;
